import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-4" }
const _hoisted_2 = { class: "w-full md:w-[240px]" }
const _hoisted_3 = { class: "flex items-center gap-2 flex-nowrap overflow-x-auto pb-1 md:flex-wrap md:overflow-visible md:pb-0" }
const _hoisted_4 = { class: "relative shrink-0" }
const _hoisted_5 = { class: "relative shrink-0" }

import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import DateFilter from '@/components/Filters/DateFilter.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useErrorToast } from '@/composables/useErrorToast'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useOperationalGroups } from '@/hooks/useOperationalGroups'
import {
  HandoverStatusFilter,
  ExpirationBucket
} from '@/Network/Types/Requests/HandoverInviteRequests'
import ScaleIcon from '@/assets/icons/scale.vue'
import HospitalIcon from '@/assets/icons/hospital.vue'
import type { HandoverFilterValues } from './handoverFilters.types'

interface DateRange {
  start: Date | null
  end: Date | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'HandoverFilters',
  props: {
    modelValue: {},
    context: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





const scheduleStore = useScheduleStore()
const { notifyError } = useErrorToast()
const healthUnitStore = useHealthUnitStore()
const { options: operationalGroupOptions, load: loadOperationalGroups } = useOperationalGroups()

const searchText = ref(props.modelValue.query ?? '')
const openFilterName = ref<string | null>(null)

const scaleIcon = ScaleIcon
const hospitalIcon = HospitalIcon

const EXPIRATION_BUCKET_LABELS: Record<ExpirationBucket, string> = {
  [ExpirationBucket.CRITICAL]: 'Crítico (menos de 24h)',
  [ExpirationBucket.WARNING]: 'Atenção (até 3 dias)',
  [ExpirationBucket.ON_TIME]: 'No prazo (mais de 3 dias)'
}

// Ordem do contrato C1: do início do fluxo (convite enviado) ao desfecho.
const STATUS_OPTIONS: { value: HandoverStatusFilter; label: string }[] = [
  { value: HandoverStatusFilter.IN_TRANSITION, label: 'Em transição' },
  { value: HandoverStatusFilter.IN_ADMIN_QUEUE, label: 'Em fila de aprovação' },
  { value: HandoverStatusFilter.APPROVED, label: 'Aprovado' },
  { value: HandoverStatusFilter.REJECTED, label: 'Reprovado' },
  // Mesmo rótulo do badge da linha: quem vê "Cancelado" na tabela precisa achar
  // a opção com esse nome no filtro.
  { value: HandoverStatusFilter.CANCELLED_BY_REQUESTER, label: 'Cancelado' },
  { value: HandoverStatusFilter.EXPIRED, label: 'Expirado' }
]

const expirationBucketOptions = Object.entries(EXPIRATION_BUCKET_LABELS).map(([value, label]) => ({
  value,
  label
}))

const statusOptions = STATUS_OPTIONS

// `listSchedules` (GET /schedules) é a lista plana de escalas de verdade.
// `schedulesForPage` (GET /schedules/list) é a árvore empresa → unidade →
// escala: o nível de topo ali é a EMPRESA, então este filtro exibia nomes de
// empresa e mandava company.id como scheduleId — o backend filtrava
// `schedule.id = <companyId>` e a listagem voltava vazia.
const scheduleOptions = computed(() =>
  scheduleStore.listSchedules.map((schedule) => ({
    label: schedule.title,
    value: schedule.value
  }))
)

// `listHealthUnits` vem de `/healthUnits/list-view`, que não é paginado.
// `healthUnits.data` vem do endpoint paginado com `limit: 15`: o dropdown
// mostrava só as 15 primeiras unidades, e como a busca do SelectFilter é
// client-side, digitar o nome de uma unidade fora dessas 15 também não
// encontrava nada.
const healthUnitOptions = computed(() =>
  healthUnitStore.listHealthUnits.map((unit) => ({
    label: unit.title,
    value: unit.value
  }))
)

const selectedScheduleId = computed(() =>
  props.modelValue.scheduleId ? String(props.modelValue.scheduleId) : null
)

const selectedHealthUnitId = computed(() =>
  props.modelValue.healthUnitId ? String(props.modelValue.healthUnitId) : null
)

const update = (patch: Partial<HandoverFilterValues>) => {
  emit('update:modelValue', { ...props.modelValue, ...patch })
}

// O SelectFilter emite um valor por vez; acumular/remover é responsabilidade daqui.
const toggleInList = <T,>(list: T[] | undefined, value: T): T[] => {
  const current = list ?? []
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
}

const toggleFilter = (name: string) => {
  openFilterName.value = openFilterName.value === name ? null : name
}

const closeFilters = () => {
  openFilterName.value = null
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null

const onSearch = () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => update({ query: searchText.value }), 400)
}

const handleShiftDateFilter = (range: DateRange) => {
  update({ startDate: range.start?.toISOString(), endDate: range.end?.toISOString() })
}

const handleExpirationDateFilter = (range: DateRange) => {
  update({
    expirationStartDate: range.start?.toISOString(),
    expirationEndDate: range.end?.toISOString()
  })
}

const handleScheduleSelect = (value: string) => update({ scheduleId: Number(value) })

const handleHealthUnitSelect = (value: string) => update({ healthUnitId: Number(value) })

const toggleOperationalGroup = (value: string) => {
  update({ operationalGroups: toggleInList(props.modelValue.operationalGroups, value) })
}

const selectExpirationBucket = (value: string) => {
  const bucket = value as ExpirationBucket
  update({ expirationBucket: props.modelValue.expirationBucket === bucket ? undefined : bucket })
}

const toggleStatus = (value: string) => {
  update({
    statuses: toggleInList(props.modelValue.statuses, value as HandoverStatusFilter)
  })
}

__expose({
  toggleOperationalGroup,
  selectExpirationBucket,
  toggleStatus,
  onSearch,
  handleShiftDateFilter,
  handleExpirationDateFilter
})

watch(
  () => props.modelValue.query,
  (query) => {
    if (query !== searchText.value) searchText.value = query ?? ''
  }
)

/**
 * Mantém a lista de escalas coerente com a unidade filtrada, no mesmo padrão do
 * Dashboard (`components/Dashboard/Header/filters/ScalesFilter.vue`): recarrega
 * as escalas da unidade e descarta a escala selecionada que deixou de pertencer
 * a ela. Sem a poda, o filtro antigo continua valendo na requisição sem aparecer
 * no dropdown — a tela volta vazia e nada na interface explica por quê.
 */
const syncSchedules = async (healthUnitId: number | undefined) => {
  if (healthUnitId) {
    await scheduleStore.setSchedulesByHealthUnits([healthUnitId])
  } else {
    await scheduleStore.getAllSchedules()
  }

  const selectedId = props.modelValue.scheduleId
  if (!selectedId) return

  const available = new Set(scheduleStore.listSchedules.map((schedule) => schedule.value))
  if (!available.has(String(selectedId))) update({ scheduleId: undefined })
}

watch(
  () => props.modelValue.healthUnitId,
  (healthUnitId) => {
    syncSchedules(healthUnitId).catch((error) =>
      notifyError(error, 'Não foi possível carregar as escalas')
    )
  }
)

onMounted(() => {
  document.addEventListener('click', closeFilters)
  // Escalas não têm cache no store: só busca se a aba ainda não tiver carregado
  // a lista, evitando refetch a cada abertura do modal de histórico. A carga
  // inicial passa pelo `syncSchedules` para já respeitar a unidade filtrada.
  if (scheduleStore.listSchedules.length === 0)
    syncSchedules(props.modelValue.healthUnitId).catch((error) =>
      notifyError(error, 'Não foi possível carregar as escalas')
    )
  // Unidades, ao contrário, carregam sempre: `listHealthUnits` é compartilhado
  // com as telas que o preenchem pelo endpoint paginado, então uma lista curta
  // deixada por outra aba passaria por completa aqui. Mesma carga do Dashboard.
  healthUnitStore.fetchAllHealthUnitsForSelect()
  loadOperationalGroups()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeFilters)
  if (debounceTimer) clearTimeout(debounceTimer)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(SearchInput, {
        modelValue: searchText.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchText).value = $event)),
        "data-testid": "handover-search-input",
        width: "100%",
        label: "Buscar",
        "hide-details": "",
        density: "compact",
        onInput: onSearch
      }, null, 8, ["modelValue"])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(DateFilter, {
          title: "Período",
          "data-testid": "handover-shift-date-filter",
          onSelect: handleShiftDateFilter
        })
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(DateFilter, {
          title: "Expiração",
          "data-testid": "handover-expiration-date-filter",
          onSelect: handleExpirationDateFilter
        })
      ]),
      _createElementVNode("div", {
        class: "relative shrink-0",
        onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (toggleFilter('unit')), ["stop"]))
      }, [
        _createVNode(SelectFilter, {
          label: "Unidades",
          "data-testid": "handover-unit-filter",
          icon: _unref(hospitalIcon),
          options: healthUnitOptions.value,
          "is-open": openFilterName.value === 'unit',
          "selected-value": selectedHealthUnitId.value,
          searchable: "",
          "dropdown-width": "w-64",
          onSelect: handleHealthUnitSelect
        }, null, 8, ["icon", "options", "is-open", "selected-value"])
      ]),
      _createElementVNode("div", {
        class: "relative shrink-0",
        onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (toggleFilter('schedule')), ["stop"]))
      }, [
        _createVNode(SelectFilter, {
          label: "Escalas",
          "data-testid": "handover-schedule-filter",
          icon: _unref(scaleIcon),
          options: scheduleOptions.value,
          "is-open": openFilterName.value === 'schedule',
          "selected-value": selectedScheduleId.value,
          searchable: "",
          "dropdown-width": "w-64",
          onSelect: handleScheduleSelect
        }, null, 8, ["icon", "options", "is-open", "selected-value"])
      ]),
      _createElementVNode("div", {
        class: "relative shrink-0",
        onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (toggleFilter('operationalGroup')), ["stop"]))
      }, [
        _createVNode(SelectFilter, {
          label: "Grupo Operacional",
          "data-testid": "handover-operational-group-filter",
          options: _unref(operationalGroupOptions),
          "is-open": openFilterName.value === 'operationalGroup',
          "selected-value": _ctx.modelValue.operationalGroups ?? [],
          multiple: "",
          searchable: "",
          "dropdown-width": "w-64",
          onSelect: toggleOperationalGroup
        }, null, 8, ["options", "is-open", "selected-value"])
      ]),
      (_ctx.context === 'pending')
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "relative shrink-0",
            onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (toggleFilter('expirationBucket')), ["stop"]))
          }, [
            _createVNode(SelectFilter, {
              label: "Situação",
              "data-testid": "handover-expiration-bucket-filter",
              options: _unref(expirationBucketOptions),
              "is-open": openFilterName.value === 'expirationBucket',
              "selected-value": _ctx.modelValue.expirationBucket ?? null,
              "dropdown-width": "w-56",
              onSelect: selectExpirationBucket
            }, null, 8, ["options", "is-open", "selected-value"])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: "relative shrink-0",
        onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (toggleFilter('status')), ["stop"]))
      }, [
        _createVNode(SelectFilter, {
          label: "Status",
          "data-testid": "handover-status-filter",
          options: _unref(statusOptions),
          "is-open": openFilterName.value === 'status',
          "selected-value": _ctx.modelValue.statuses ?? [],
          multiple: "",
          "dropdown-width": "w-56",
          "align-right": "",
          onSelect: toggleStatus
        }, null, 8, ["options", "is-open", "selected-value"])
      ])
    ])
  ]))
}
}

})